import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  Plus, 
  Edit,
  Trash2,
  FileText,
  X
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchCategories, createCategory, updateCategory, deleteCategory } from '../services/newsApi';

const AdminCategories = () => {
  const { isDarkMode } = useTheme();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    color: '#4CAF50'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await fetchCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      alert('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      if (name === 'name' && !editingCategory) {
        updated.slug = generateSlug(value);
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Category name is required');
      return;
    }

    try {
      setSubmitting(true);

      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
        alert('Category updated successfully!');
      } else {
        await createCategory(formData);
        alert('Category created successfully!');
      }
      
      loadCategories();
      resetForm();
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error.message || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      color: category.color || '#4CAF50'
    });
    setShowForm(true);
  };

  const handleDelete = async (category) => {
    if (category.post_count > 0) {
      alert(`Cannot delete category "${category.name}" because it has ${category.post_count} posts. Please reassign or delete those posts first.`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
      return;
    }

    try {
      await deleteCategory(category.id);
      alert('Category deleted successfully');
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert(error.message || 'Failed to delete category');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      color: '#4CAF50'
    });
    setEditingCategory(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
      }`}>
        <div className="text-center">
          <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
            isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
          }`}></div>
          <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
            Loading categories...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-silver-50'
    }`}>
      <div className="container-custom py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-3xl sm:text-4xl font-bold font-cursive ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Post Categories
            </h1>
            <p className={`text-sm mt-1 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Organize your news posts with categories
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary inline-flex items-center justify-center"
          >
            {showForm ? (
              <>
                <X className="w-5 h-5 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="w-5 h-5 mr-2" />
                Add Category
              </>
            )}
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div className={`p-6 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h2 className={`text-lg font-semibold mb-6 ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              {editingCategory ? 'Edit Category' : 'New Category'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Name <span className="text-fsgu-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input"
                    placeholder="e.g., Tournaments"
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Slug
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="input"
                    placeholder="tournaments"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                    className="input"
                    placeholder="Brief description of this category"
                  />
                </div>

                {/* Color */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                  }`}>
                    Badge Color
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      name="color"
                      value={formData.color}
                      onChange={handleChange}
                      className="h-11 w-20 rounded-lg cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="input flex-1"
                      placeholder="#4CAF50"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-6 border-t border-fsgu-gray-700">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    editingCategory ? 'Update Category' : 'Create Category'
                  )}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Categories Grid */}
        {categories.length === 0 ? (
          <div className={`text-center py-16 rounded-xl border ${
            isDarkMode 
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <Tag className={`w-20 h-20 mx-auto mb-4 ${
              isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
            }`} />
            <h3 className={`text-xl font-semibold mb-2 ${
              isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
            }`}>
              No categories yet
            </h3>
            <p className={`text-sm ${
              isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'
            }`}>
              Create your first category to organize posts
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map(category => (
              <div
                key={category.id}
                className={`p-6 rounded-xl border transition-all hover:shadow-lg ${
                  isDarkMode 
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700' 
                    : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span 
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white whitespace-nowrap"
                        style={{ backgroundColor: category.color }}
                      >
                        {category.name}
                      </span>
                      <span className={`text-xs ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        /{category.slug}
                      </span>
                    </div>
                    {category.description && (
                      <p className={`text-sm line-clamp-2 ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}>
                        {category.description}
                      </p>
                    )}
                  </div>
                  <div className={`p-2 rounded-lg ${
                    isDarkMode ? 'bg-fsgu-green-500/20' : 'bg-fsgu-green-500/10'
                  }`}>
                    <Tag className={`w-5 h-5 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                  </div>
                </div>

                <div className={`flex items-center justify-between pt-4 border-t ${
                  isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
                }`}>
                  <div className="flex items-center gap-1.5 text-sm">
                    <FileText className={`w-4 h-4 ${
                      isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                    }`} />
                    <span className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                      {category.post_count || 0} {category.post_count === 1 ? 'post' : 'posts'}
                    </span>
                  </div>

                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(category)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'hover:bg-fsgu-gray-700 text-fsgu-silver-400'
                          : 'hover:bg-fsgu-silver-100 text-fsgu-silver-600'
                      }`}
                      title="Edit category"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(category)}
                      className={`p-2 rounded-lg transition-colors ${
                        isDarkMode
                          ? 'hover:bg-fsgu-red-900/20 text-fsgu-red-400'
                          : 'hover:bg-fsgu-red-50 text-fsgu-red-600'
                      }`}
                      title="Delete category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCategories;